import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { ShieldCheck } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CODES = [
  ['PASS', 'bg-emerald-100 text-emerald-700', 'Check completed and everything verified'],
  ['FAIL', 'bg-red-100 text-red-700', 'Completed negative (e.g. ID not found / account closed / identity mismatch)'],
  ['REVIEW', 'bg-amber-100 text-amber-800', 'Name mismatch, missing or ambiguous result — needs a human decision'],
  ['ERROR', 'bg-orange-100 text-orange-700', 'Technical: bad key (401), no credits (402), conflict (409), rate limit (429), timeout/outage'],
  ['PENDING', 'bg-blue-100 text-blue-700', 'Request in flight'],
  ['NOT_CHECKED', 'bg-slate-100 text-slate-600', 'Never checked, or details changed since the last check'],
];

export const VerifyNowSettings = ({ getAuthHeader }) => {
  const [cfg, setCfg] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [credits, setCredits] = useState(null);

  const loadCredits = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/verifynow/credit-balance`, getAuthHeader());
      setCredits(res.data);
    } catch { setCredits({ ok: false, reason: 'Could not fetch balance' }); }
  }, [getAuthHeader]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/verifynow/settings`, getAuthHeader());
      setCfg(res.data);
      if (res.data.api_key_set) loadCredits();
    } catch { /* ignore */ }
  }, [getAuthHeader, loadCredits]);

  useEffect(() => { load(); }, [load]);

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const body = { ...extra };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const res = await axios.put(`${API_URL}/admin/verifynow/settings`, body, getAuthHeader());
      setCfg(res.data);
      setApiKey('');
      toast.success('VerifyNow settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  if (!cfg) return null;
  return (
    <Card className="border-l-4 border-l-indigo-500" data-testid="verifynow-settings-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-indigo-600" />VerifyNow — ID & Bank Verification
          <Badge variant="outline" className={`text-[10px] border-0 ${cfg.mode === 'production' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}`}>{cfg.mode.toUpperCase()}</Badge>
          <Badge variant="outline" className={`text-[10px] border-0 ${cfg.api_key_set ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`} data-testid="vn-key-status">
            {cfg.api_key_set ? `Key set (${cfg.api_key_masked})` : 'No API key'}
          </Badge>
          {cfg.api_key_set && credits && (
            credits.ok && credits.balance != null ? (
              <Badge variant="outline" className={`text-[10px] border-0 cursor-pointer ${credits.balance < 10 ? 'bg-red-100 text-red-700' : credits.balance < 50 ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-700'}`}
                onClick={loadCredits} title={`Click to refresh${credits.last_refreshed ? ` · last refreshed ${credits.last_refreshed}` : ''}${credits.balance < 10 ? ' · LOW — top up before checks fail' : ''}`} data-testid="vn-credit-balance">
                Credits: {credits.balance}{credits.balance < 10 ? ' — LOW, top up!' : ''}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] border-0 bg-slate-100 text-slate-500 cursor-pointer" onClick={loadCredits} title={credits.reason || ''} data-testid="vn-credit-balance">
                Credits: unavailable
              </Badge>
            )
          )}
        </CardTitle>
        <p className="text-xs text-muted-foreground">The key is stored server-side only. New applications (from {cfg.autocheck_from}) must pass ID + bank checks before approval. Sandbox results never qualify a live payout.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <p className="text-[11px] font-semibold text-slate-600">API Key</p>
            <Input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder={cfg.api_key_set ? 'Enter new key to replace' : 'Paste your VerifyNow API key'} className="h-9 w-[280px] text-sm" data-testid="vn-api-key-input" />
          </div>
          <div className="space-y-1">
            <p className="text-[11px] font-semibold text-slate-600">Mode</p>
            <Select value={cfg.mode} onValueChange={v => save({ mode: v })}>
              <SelectTrigger className="h-9 w-[140px] text-sm" data-testid="vn-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sandbox">Sandbox</SelectItem>
                <SelectItem value="production">Production</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <p className="text-[11px] font-semibold text-slate-600">Gate new apps from</p>
            <Input type="date" value={cfg.autocheck_from} onChange={e => save({ autocheck_from: e.target.value })} className="h-9 w-[150px] text-sm" data-testid="vn-autocheck-from" />
          </div>
          <div className="space-y-1">
            <p className="text-[11px] font-semibold text-slate-600">Block payouts without production PASS</p>
            <div className="h-9 flex items-center"><Switch checked={cfg.enforce_payout} onCheckedChange={v => save({ enforce_payout: v })} data-testid="vn-enforce-switch" /></div>
          </div>
          <Button onClick={() => save()} disabled={saving || !apiKey.trim()} className="h-9 bg-indigo-600 hover:bg-indigo-700" data-testid="vn-save-key-btn">
            {saving ? 'Saving…' : 'Save Key'}
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 p-3 bg-slate-50 rounded-lg" data-testid="vn-codes-reference">
          {CODES.map(([code, cls, desc]) => (
            <div key={code} className="flex items-start gap-2 text-[11px] text-slate-600">
              <Badge variant="outline" className={`border-0 text-[9px] shrink-0 ${cls}`}>{code}</Badge>
              <span>{desc}</span>
            </div>
          ))}
        </div>
        {cfg.updated_by && <p className="text-[10px] text-slate-400">Last updated by {cfg.updated_by} · {cfg.updated_at ? new Date(cfg.updated_at).toLocaleString('en-ZA') : ''}</p>}
      </CardContent>
    </Card>
  );
};
